import {
  fileRecordListQuery,
  fileRecordListScope,
  workspaceTreeQuery,
} from "@/api/fileRecords";
import { getDirectChildren } from "@/pages/workspaces/utils/folderPaths";
import { getPlannedUploadFile } from "@/utils/imageFilePrep";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { FileConflictDialog } from "./FileConflictDialog";
import type {
  ConflictItem,
  ConflictPromptResult,
  ConflictResolution,
  IntendedCopy,
  IntendedFolderUpload,
  IntendedMove,
  IntendedUpload,
  ResolvedCopyOperation,
  ResolvedFolderUpload,
  ResolvedOperation,
  ResolvedUpload,
  UploadConflictScope,
} from "./types";
import { generateUniqueName } from "./uniqueName";

interface ResolverContextValue {
  /**
   * Reconcile a batch of intended moves against the destination workspace.
   * Returns the concrete operation list, or `null` if the user cancelled the
   * batch. `sourceRecords` must contain every record belonging to the source
   * workspace so folder merges can enumerate their children.
   *
   * Move is workspace-only — chat and canvas uploads don't have a "move
   * between scopes" UX, so this resolver only ever runs against workspace
   * destinations. The API stays here so all conflict-resolution paths
   * share a single dialog provider.
   */
  resolveMoves: (params: {
    moves: IntendedMove[];
    sourceRecords: FileRecord[];
    destinationWorkspaceId: string;
  }) => Promise<ResolvedOperation[] | null>;

  /**
   * Reconcile a batch of intended uploads against any class scope
   * (workspace, canvas, or flow-session). Returns the list of uploads to
   * perform (renamed for Keep both, flagged with `overwrite: true` for
   * Replace contents, or dropped on Skip), or `null` if the user cancelled
   * the batch.
   */
  resolveUploads: (params: {
    uploads: IntendedUpload[];
    scope: UploadConflictScope;
  }) => Promise<ResolvedUpload[] | null>;

  /**
   * Reconcile a batch of intended cross-scope copies against their
   * destinations. Mirrors `resolveMoves` but always emits copy operations
   * (the source record is preserved).
   */
  resolveCopies: (params: {
    copies: IntendedCopy[];
    sourceRecords: FileRecord[];
  }) => Promise<ResolvedCopyOperation[] | null>;

  /**
   * Reconcile a recursive folder upload (OS drag-drop of one or more
   * directories) against a workspace. Walks the tree shallow-to-deep,
   * prompting on folder-level collisions (Merge / Keep both / Skip)
   * and file-level collisions (Replace / Keep both / Skip). When a
   * folder is renamed via Keep both, all descendant paths are
   * rewritten under the new prefix; when a folder subtree is skipped,
   * every descendant is dropped without further prompts.
   *
   * Workspace-only — folder uploads don't have semantics on the canvas
   * / flow-session sides where paths are bare leaves.
   */
  resolveFolderUpload: (params: {
    tree: IntendedFolderUpload;
    parentPath: string;
    workspaceId: string;
  }) => Promise<ResolvedFolderUpload | null>;
}

/**
 * react-query key + list call for the destination scope's existing records.
 * Centralized so a future class doesn't need to grow new branches in the
 * resolver body.
 */
function destinationListConfig(scope: UploadConflictScope): {
  queryKey: readonly unknown[];
  queryFn: () => Promise<FileRecord[]>;
} {
  return fileRecordListQuery(scope);
}

const Context = createContext<ResolverContextValue | null>(null);

interface ActivePrompt {
  conflict: ConflictItem;
  remaining: number;
  /** Selects dialog wording for move/upload-like operations vs copies. */
  verb: "move" | "copy";
  resolve: (result: ConflictPromptResult | "cancel") => void;
}

/** Internal queue entry; adds merge-parent tracking to IntendedMove. */
interface QueueItem extends IntendedMove {
  /** Source folder id this item was expanded from (if any). */
  mergeParentId?: string;
}

interface MergeBookkeeping {
  sourceFolderId: string;
  anySkipped: boolean;
}

function buildFullPath(move: IntendedMove): string {
  const leaf = move.isFolder ? `${move.sourceName}/` : move.sourceName;
  return `${move.destinationParentPath}${leaf}`;
}

function leafFromPath(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segs = cleaned.split("/").filter(Boolean);
  return segs[segs.length - 1] ?? "";
}

function parentOfPath(path: string): string {
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segs = cleaned.split("/").filter(Boolean);
  if (segs.length <= 1) return "";
  return segs.slice(0, -1).join("/") + "/";
}

export function FileConflictResolverProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const [active, setActive] = useState<ActivePrompt | null>(null);
  const promptRef = useRef<ActivePrompt | null>(null);

  const promptConflict = useCallback(
    (
      conflict: ConflictItem,
      remaining: number,
      verb: "move" | "copy" = "move",
    ) =>
      new Promise<ConflictPromptResult | "cancel">((resolve) => {
        const next: ActivePrompt = { conflict, remaining, verb, resolve };
        promptRef.current = next;
        setActive(next);
      }),
    [],
  );

  const handleResolve = (
    resolution: ConflictResolution,
    applyToAll: boolean,
  ) => {
    const cur = promptRef.current;
    promptRef.current = null;
    setActive(null);
    cur?.resolve({ resolution, applyToAll });
  };

  const handleCancel = () => {
    const cur = promptRef.current;
    promptRef.current = null;
    setActive(null);
    cur?.resolve("cancel");
  };

  const resolveMoves = useCallback<ResolverContextValue["resolveMoves"]>(
    async ({ moves, sourceRecords, destinationWorkspaceId }) => {
      if (moves.length === 0) return [];

      // Pull the destination workspace's records (cached or fresh).
      const destinationRecords = await queryClient.fetchQuery(
        workspaceTreeQuery(destinationWorkspaceId),
      );

      const takenPaths = new Set(destinationRecords.map((r) => r.path));
      const recordByPath = new Map(
        destinationRecords.map((r) => [r.path, r] as const),
      );

      // "Kind-aware" takenness. A file "X" and a folder "X/" can coexist
      // because they live at different paths — we only check exact paths.

      // FIFO queue: seed with the initial moves.
      const queue: QueueItem[] = [...moves];

      // When the user picks "Apply to all", subsequent conflicts of the same
      // kind (file vs folder) are auto-resolved without re-prompting.
      let appliedFileAll: ConflictResolution | null = null;
      let appliedFolderAll: ConflictResolution | null = null;

      /** Bookkeeping so we can delete source folders that were fully merged. */
      const mergeBookByParent = new Map<string, MergeBookkeeping>();
      const ops: ResolvedOperation[] = [];

      const buildMovePayload = (path: string, isFolder: boolean) => ({
        destination: {
          class: "workspace" as const,
          workspace_id: destinationWorkspaceId,
          path,
          is_folder: isFolder || undefined,
        },
      });

      // Count the initial set of conflicts so the dialog can show remaining N.
      // (Merge-expanded children may add more conflicts later; we increment
      // the counter when they are enqueued.)
      let remainingConflicts = moves.filter((m) =>
        takenPaths.has(buildFullPath(m)),
      ).length;

      while (queue.length > 0) {
        const move = queue.shift()!;
        const fullPath = buildFullPath(move);

        // No collision → straight move. Mark the destination path as taken
        // so a later keep-both in the same batch doesn't pick the same leaf.
        if (!takenPaths.has(fullPath)) {
          takenPaths.add(fullPath);
          ops.push({
            kind: "move",
            fileRecordId: move.fileRecordId,
            payload: buildMovePayload(fullPath, move.isFolder),
          });
          continue;
        }

        // Collision. Resolve.
        const existing = recordByPath.get(fullPath);

        // Guard: cross-kind collisions should not happen (different path
        // representations), but if the backend ever produced one, treat it
        // as a skip rather than crash.
        if (existing && existing.path.endsWith("/") !== move.isFolder) {
          continue;
        }

        let resolution: ConflictResolution;
        const appliedAll = move.isFolder ? appliedFolderAll : appliedFileAll;
        if (appliedAll) {
          resolution = appliedAll;
        } else {
          const result = await promptConflict(
            { sourceName: move.sourceName, isFolder: move.isFolder },
            remainingConflicts,
          );
          if (result === "cancel") return null;
          resolution = result.resolution;
          if (result.applyToAll) {
            if (move.isFolder) appliedFolderAll = resolution;
            else appliedFileAll = resolution;
          }
        }
        remainingConflicts = Math.max(0, remainingConflicts - 1);

        // If the "Apply to all" selection is semantically invalid for the
        // current item's kind (e.g. user chose merge on a folder then the
        // next item is a file), fall back to keep_both.
        if (
          (resolution === "replace_contents" && move.isFolder) ||
          (resolution === "merge" && !move.isFolder)
        ) {
          resolution = "keep_both";
        }

        if (resolution === "skip") {
          if (move.mergeParentId) {
            const book = mergeBookByParent.get(move.mergeParentId);
            if (book) book.anySkipped = true;
          }
          continue;
        }

        if (resolution === "keep_both") {
          const siblingNames = new Set<string>();
          for (const path of takenPaths) {
            if (parentOfPath(path) === move.destinationParentPath) {
              siblingNames.add(leafFromPath(path));
            }
          }
          const newLeaf = generateUniqueName(
            move.sourceName,
            move.isFolder,
            siblingNames,
          );
          const newFullPath = `${move.destinationParentPath}${
            move.isFolder ? `${newLeaf}/` : newLeaf
          }`;
          takenPaths.add(newFullPath);
          ops.push({
            kind: "move",
            fileRecordId: move.fileRecordId,
            payload: buildMovePayload(newFullPath, move.isFolder),
          });
          continue;
        }

        if (resolution === "replace_contents") {
          // Files only. Destination id and path survive; source is untouched.
          // Implemented server-side as `POST /file-records/:source.id/copy`
          // with `overwrite: true` — the upsert preserves the destination
          // record at this path while content fields are replaced.
          if (!existing) continue;
          ops.push({
            kind: "replace_contents",
            sourceFileRecordId: move.fileRecordId,
            payload: {
              overwrite: true,
              destination: {
                class: "workspace",
                workspace_id: destinationWorkspaceId,
                path: fullPath,
                is_folder: undefined,
              },
            },
          });
          // The source file is intentionally preserved, so a folder merge
          // that hits this branch can't delete its parent source folder.
          if (move.mergeParentId) {
            const book = mergeBookByParent.get(move.mergeParentId);
            if (book) book.anySkipped = true;
          }
          continue;
        }

        if (resolution === "merge") {
          // Folder-on-folder merge. Expand the source folder's direct
          // children into new moves that target the destination folder path.
          const sourceFolder = sourceRecords.find(
            (r) => r.id === move.fileRecordId,
          );
          if (!sourceFolder) continue;

          const destinationFolderPath = fullPath; // ends in "/"
          const childRecords = getDirectChildren(
            sourceRecords,
            sourceFolder.path,
          );

          // Track this folder so we can decide about post-merge cleanup.
          mergeBookByParent.set(move.fileRecordId, {
            sourceFolderId: move.fileRecordId,
            anySkipped: childRecords.length === 0 ? true : false,
          });

          for (const child of childRecords) {
            // A canvas never collides (its leaf is its id) and never goes
            // through the file-record move — re-file it through the canvas API.
            if (child.kind === "canvas") {
              const canvasId = child.canvas?.id;
              if (canvasId) {
                ops.push({
                  kind: "move_canvas",
                  canvasId,
                  destinationParentPath: destinationFolderPath,
                  workspaceId: destinationWorkspaceId,
                });
              }
              continue;
            }
            const childIsFolder = child.path.endsWith("/");
            const childLeaf = leafFromPath(child.path);
            const childMove: QueueItem = {
              fileRecordId: child.id,
              sourceName: childLeaf,
              destinationParentPath: destinationFolderPath,
              isFolder: childIsFolder,
              mergeParentId: move.fileRecordId,
            };
            queue.push(childMove);
            // If this child will itself collide, include it in the counter.
            if (takenPaths.has(buildFullPath(childMove))) {
              remainingConflicts += 1;
            }
          }
          continue;
        }
      }

      // Any fully-merged source folder (no skipped children, no replace
      // leftovers) is now logically empty at the source — delete it.
      for (const book of mergeBookByParent.values()) {
        if (!book.anySkipped) {
          ops.push({ kind: "delete", fileRecordId: book.sourceFolderId });
        }
      }

      return ops;
    },
    [queryClient, promptConflict],
  );

  const resolveUploads = useCallback<ResolverContextValue["resolveUploads"]>(
    async ({ uploads, scope }) => {
      if (uploads.length === 0) return [];

      // Plan the final upload name/type without doing expensive byte work.
      // HEIC / HEIF will become JPEG before upload, but conflict checks only
      // need the deterministic post-conversion leaf (`foo.heic` -> `foo.jpg`).
      // The actual HEIC decode stays in the byte primitive so canvas /
      // workspace ghosts can render immediately after conflict resolution.
      const plannedUploads = uploads.map((u) => ({
        ...u,
        planned: getPlannedUploadFile(u.file),
      }));

      // Pull the destination scope's records (cached or fresh). Using
      // react-query's shared cache means the dialog sees the same truth the
      // page is rendering — no extra round-trip if it's fresh.
      const listConfig = destinationListConfig(scope);
      const destinationRecords = await queryClient.fetchQuery<FileRecord[]>({
        queryKey: listConfig.queryKey,
        queryFn: listConfig.queryFn,
      });

      // Exclude tombstones — they're not visible to the user and would
      // produce phantom conflicts.
      const livePaths = destinationRecords.filter(
        (r) => r.status !== "deleted",
      );
      const takenPaths = new Set(livePaths.map((r) => r.path));
      const recordByPath = new Map(livePaths.map((r) => [r.path, r] as const));

      let remainingConflicts = plannedUploads.filter((u) =>
        takenPaths.has(`${u.destinationParentPath}${u.planned.name}`),
      ).length;

      let appliedAll: ConflictResolution | null = null;
      const resolved: ResolvedUpload[] = [];

      for (const upload of plannedUploads) {
        const fullPath = `${upload.destinationParentPath}${upload.planned.name}`;

        // No collision — just upload.
        if (!takenPaths.has(fullPath)) {
          takenPaths.add(fullPath);
          resolved.push({
            kind: "create",
            file: upload.file,
            name: upload.planned.name,
            contentType: upload.planned.contentType,
            path: fullPath,
          });
          continue;
        }

        let resolution: ConflictResolution;
        if (appliedAll) {
          resolution = appliedAll;
        } else {
          const result = await promptConflict(
            { sourceName: upload.planned.name, isFolder: false },
            remainingConflicts,
          );
          if (result === "cancel") return null;
          resolution = result.resolution;
          if (result.applyToAll) appliedAll = resolution;
        }
        remainingConflicts = Math.max(0, remainingConflicts - 1);

        // "merge" is never valid for file uploads; coerce to keep_both so
        // an Apply-to-all selection carried over from a folder conflict
        // doesn't surprise the user.
        if (resolution === "merge") resolution = "keep_both";

        if (resolution === "skip") continue;

        if (resolution === "keep_both") {
          const siblingNames = new Set<string>();
          for (const path of takenPaths) {
            if (parentOfPath(path) === upload.destinationParentPath) {
              siblingNames.add(leafFromPath(path));
            }
          }
          const newLeaf = generateUniqueName(
            upload.planned.name,
            false,
            siblingNames,
          );
          const newFullPath = `${upload.destinationParentPath}${newLeaf}`;
          takenPaths.add(newFullPath);
          resolved.push({
            kind: "create",
            file: upload.file,
            name: newLeaf,
            contentType: upload.planned.contentType,
            path: newFullPath,
          });
          continue;
        }

        if (resolution === "replace_contents") {
          // Re-mint at the same destination path with overwrite=true. The
          // server upserts the existing record in place — id, share URLs,
          // and publish state survive while content fields are replaced —
          // and returns a fresh presigned S3 POST URL the queue uploads to.
          const existing = recordByPath.get(fullPath);
          if (!existing) continue;
          resolved.push({
            kind: "replace",
            file: upload.file,
            name: upload.planned.name,
            contentType: upload.planned.contentType,
            path: fullPath,
          });
          continue;
        }
      }

      return resolved;
    },
    [queryClient, promptConflict],
  );

  const resolveCopies = useCallback<ResolverContextValue["resolveCopies"]>(
    async ({ copies, sourceRecords }) => {
      if (copies.length === 0) return [];

      // A single batch can contain copies that target multiple distinct
      // destination scopes. Paths collide only within their own scope.
      const fetchScopeRecords = async (
        components: IntendedCopy["destination"]["components"],
      ): Promise<FileRecord[]> => {
        const scope = fileRecordListScope(components);
        return scope ? queryClient.fetchQuery(fileRecordListQuery(scope)) : [];
      };

      const scopeKey = (
        components: IntendedCopy["destination"]["components"],
      ): string => {
        if (components.class === "workspace") {
          return `workspace:${components.workspace_id ?? ""}`;
        }
        if (components.class === "canvas") {
          return `canvas:${components.canvas_id ?? ""}`;
        }
        return "unknown:";
      };

      const scopeCache = new Map<
        string,
        { taken: Set<string>; recordByPath: Map<string, FileRecord> }
      >();
      for (const copy of copies) {
        const key = scopeKey(copy.destination.components);
        if (scopeCache.has(key)) continue;
        const records = await fetchScopeRecords(copy.destination.components);
        // Exclude tombstones — they're invisible to the user and would produce
        // phantom conflicts (mirrors resolveUploads).
        const livePaths = records.filter((r) => r.status !== "deleted");
        scopeCache.set(key, {
          taken: new Set(livePaths.map((r) => r.path)),
          recordByPath: new Map(livePaths.map((r) => [r.path, r] as const)),
        });
      }

      const buildCopyFullPath = (copy: IntendedCopy): string => {
        const leaf = copy.isFolder ? `${copy.sourceName}/` : copy.sourceName;
        return `${copy.destination.parentPath}${leaf}`;
      };

      const isSameScope = (copy: IntendedCopy): boolean => {
        const source = copy.source;
        const destination = copy.destination.components;
        if (source.class !== destination.class) return false;
        if (source.class === "workspace") {
          return source.workspace_id === destination.workspace_id;
        }
        if (source.class === "canvas") {
          return source.canvas_id === destination.canvas_id;
        }
        return false;
      };

      interface CopyQueueItem extends IntendedCopy {
        mergeParentId?: string;
      }

      const queue: CopyQueueItem[] = copies.filter(
        (copy) => !isSameScope(copy),
      );
      const ops: ResolvedCopyOperation[] = [];

      let appliedFileAll: ConflictResolution | null = null;
      let appliedFolderAll: ConflictResolution | null = null;
      let remainingConflicts = queue.filter((copy) => {
        const cache = scopeCache.get(scopeKey(copy.destination.components));
        return cache?.taken.has(buildCopyFullPath(copy)) ?? false;
      }).length;

      while (queue.length > 0) {
        const copy = queue.shift()!;
        const cache = scopeCache.get(scopeKey(copy.destination.components));
        if (!cache) continue;

        const fullPath = buildCopyFullPath(copy);

        if (!cache.taken.has(fullPath)) {
          cache.taken.add(fullPath);
          ops.push({
            kind: "copy",
            sourceFileRecordId: copy.fileRecordId,
            payload: {
              destination: {
                ...copy.destination.components,
                path: fullPath,
                is_folder: copy.isFolder || undefined,
              },
            },
          });
          continue;
        }

        const existing = cache.recordByPath.get(fullPath);
        if (existing && existing.path.endsWith("/") !== copy.isFolder) {
          continue;
        }

        let resolution: ConflictResolution;
        const appliedAll = copy.isFolder ? appliedFolderAll : appliedFileAll;
        if (appliedAll) {
          resolution = appliedAll;
        } else {
          const result = await promptConflict(
            { sourceName: copy.sourceName, isFolder: copy.isFolder },
            remainingConflicts,
            "copy",
          );
          if (result === "cancel") return null;
          resolution = result.resolution;
          if (result.applyToAll) {
            if (copy.isFolder) appliedFolderAll = resolution;
            else appliedFileAll = resolution;
          }
        }
        remainingConflicts = Math.max(0, remainingConflicts - 1);

        if (
          (resolution === "replace_contents" && copy.isFolder) ||
          (resolution === "merge" && !copy.isFolder)
        ) {
          resolution = "keep_both";
        }

        if (resolution === "skip") continue;

        if (resolution === "keep_both") {
          const siblingNames = new Set<string>();
          for (const path of cache.taken) {
            if (parentOfPath(path) === copy.destination.parentPath) {
              siblingNames.add(leafFromPath(path));
            }
          }
          const newLeaf = generateUniqueName(
            copy.sourceName,
            copy.isFolder,
            siblingNames,
          );
          const newFullPath = `${copy.destination.parentPath}${
            copy.isFolder ? `${newLeaf}/` : newLeaf
          }`;
          cache.taken.add(newFullPath);
          ops.push({
            kind: "copy",
            sourceFileRecordId: copy.fileRecordId,
            payload: {
              destination: {
                ...copy.destination.components,
                path: newFullPath,
                is_folder: copy.isFolder || undefined,
              },
            },
          });
          continue;
        }

        if (resolution === "replace_contents") {
          if (!existing) continue;
          ops.push({
            kind: "copy_overwrite",
            sourceFileRecordId: copy.fileRecordId,
            payload: {
              overwrite: true,
              destination: {
                ...copy.destination.components,
                path: fullPath,
                is_folder: undefined,
              },
            },
          });
          continue;
        }

        if (resolution === "merge") {
          const sourceFolder = sourceRecords.find(
            (record) => record.id === copy.fileRecordId,
          );
          if (!sourceFolder) continue;

          const destinationFolderPath = fullPath;
          const childRecords = getDirectChildren(
            sourceRecords,
            sourceFolder.path,
          );
          for (const child of childRecords) {
            const childIsFolder = child.path.endsWith("/");
            const childLeaf = leafFromPath(child.path);
            const childCopy: CopyQueueItem = {
              fileRecordId: child.id,
              sourceName: childLeaf,
              isFolder: childIsFolder,
              source: copy.source,
              destination: {
                components: copy.destination.components,
                parentPath: destinationFolderPath,
              },
              mergeParentId: copy.fileRecordId,
            };
            queue.push(childCopy);
            if (cache.taken.has(buildCopyFullPath(childCopy))) {
              remainingConflicts += 1;
            }
          }
        }
      }

      return ops;
    },
    [queryClient, promptConflict],
  );

  const resolveFolderUpload = useCallback<
    ResolverContextValue["resolveFolderUpload"]
  >(
    async ({ tree, parentPath, workspaceId }) => {
      if (tree.folders.length === 0 && tree.files.length === 0) {
        return { folderOps: [], fileOps: [] };
      }

      const destinationRecords = await queryClient.fetchQuery(
        workspaceTreeQuery(workspaceId),
      );
      const livePaths = destinationRecords.filter(
        (r) => r.status !== "deleted",
      );
      const takenPaths = new Set(livePaths.map((r) => r.path));
      const recordByPath = new Map(livePaths.map((r) => [r.path, r] as const));

      // Folder relative paths get a "remap" entry once we've decided
      // their final destination prefix (identity for no-collision /
      // merge, or a renamed leaf for keep-both). Files look their
      // immediate parent folder up here to compute the final path.
      const folderRemap = new Map<string, string>();
      // Sources whose entire subtree was skipped — files / nested
      // folders below any of these prefixes are silently dropped.
      const skippedPrefixes: string[] = [];
      const isInsideSkippedPrefix = (relPath: string): boolean =>
        skippedPrefixes.some((prefix) => relPath.startsWith(prefix));

      // Helpers for working with relative paths.
      // - folder rel paths always end with "/"
      // - file rel paths never end with "/"
      const parentRelOfFolder = (folderRel: string): string => {
        const trimmed = folderRel.slice(0, -1);
        const idx = trimmed.lastIndexOf("/");
        return idx === -1 ? "" : trimmed.slice(0, idx + 1);
      };
      const parentRelOfFile = (fileRel: string): string => {
        const idx = fileRel.lastIndexOf("/");
        return idx === -1 ? "" : fileRel.slice(0, idx + 1);
      };
      const leafOfFolder = (folderRel: string): string => {
        const trimmed = folderRel.slice(0, -1);
        const idx = trimmed.lastIndexOf("/");
        return idx === -1 ? trimmed : trimmed.slice(idx + 1);
      };

      const folderOps: { path: string }[] = [];
      const fileOps: ResolvedUpload[] = [];

      // Sort folders shallow-to-deep so a parent's remap is settled
      // before a child needs to look it up.
      const sortedFolders = [...tree.folders].sort((a, b) => {
        const da = (a.match(/\//g) ?? []).length;
        const db = (b.match(/\//g) ?? []).length;
        return da - db;
      });

      let appliedFolderAll: ConflictResolution | null = null;
      let appliedFileAll: ConflictResolution | null = null;

      // Pre-compute the conflict count so the dialog's "N remaining"
      // counter is honest. We can be conservative — uncollided merges
      // / unique paths simply don't fire prompts.
      const computeInitialFolderConflicts = (): number => {
        let count = 0;
        const probeRemap = new Map<string, string>();
        for (const folderRel of sortedFolders) {
          const parentRel = parentRelOfFolder(folderRel);
          const parentMapped = probeRemap.get(parentRel) ?? parentRel;
          const candidate = `${parentPath}${parentMapped}${leafOfFolder(folderRel)}/`;
          if (takenPaths.has(candidate)) count += 1;
          // For the probe we assume merge / no-rename so children look
          // up an identity-mapped parent. Underestimates conflicts in
          // the keep-both case but never overestimates.
          probeRemap.set(
            folderRel,
            `${parentMapped}${leafOfFolder(folderRel)}/`,
          );
        }
        return count;
      };
      const computeInitialFileConflicts = (): number => {
        // Probed against the unrenamed paths — same caveat as folders.
        let count = 0;
        for (const { file, relativePath } of tree.files) {
          const planned = getPlannedUploadFile(file);
          const parentRel = parentRelOfFile(relativePath);
          const candidate = `${parentPath}${parentRel}${planned.name}`;
          if (takenPaths.has(candidate)) count += 1;
        }
        return count;
      };
      let remainingFolderConflicts = computeInitialFolderConflicts();
      let remainingFileConflicts = computeInitialFileConflicts();

      // ── Phase 1: folders ────────────────────────────────────────
      for (const folderRel of sortedFolders) {
        if (isInsideSkippedPrefix(folderRel)) continue;
        const parentRel = parentRelOfFolder(folderRel);
        const parentMapped = folderRemap.get(parentRel) ?? parentRel;
        const leaf = leafOfFolder(folderRel);
        const candidatePath = `${parentPath}${parentMapped}${leaf}/`;

        if (!takenPaths.has(candidatePath)) {
          takenPaths.add(candidatePath);
          folderOps.push({ path: candidatePath });
          folderRemap.set(folderRel, `${parentMapped}${leaf}/`);
          continue;
        }

        // Collision. The existing record at this path could be a file
        // (different kind) — fall back to keep_both in that case to
        // avoid offering "Merge" against a file.
        const existing = recordByPath.get(candidatePath);
        const existingIsFolder = existing?.path.endsWith("/") ?? true;
        if (!existingIsFolder) {
          // File at the same path (without trailing slash) would be
          // `${parentPath}${parentMapped}${leaf}` — but we already
          // matched on the trailing-slash variant. This branch is
          // defensive: if the server ever co-locates them, fall
          // through to keep-both to dodge a confusing prompt.
        }

        let resolution: ConflictResolution;
        if (appliedFolderAll) {
          resolution = appliedFolderAll;
        } else {
          const result = await promptConflict(
            { sourceName: leaf, isFolder: true },
            remainingFolderConflicts,
          );
          if (result === "cancel") return null;
          resolution = result.resolution;
          if (result.applyToAll) appliedFolderAll = resolution;
        }
        remainingFolderConflicts = Math.max(0, remainingFolderConflicts - 1);

        // Coerce semantically-invalid choices into safe defaults.
        if (resolution === "replace_contents") resolution = "keep_both";

        if (resolution === "skip") {
          skippedPrefixes.push(folderRel);
          continue;
        }
        if (resolution === "merge") {
          // Re-use the existing destination folder — no folderOp emitted,
          // but children still flow into this prefix.
          folderRemap.set(folderRel, `${parentMapped}${leaf}/`);
          continue;
        }
        if (resolution === "keep_both") {
          const siblingNames = new Set<string>();
          const fullParentPath = `${parentPath}${parentMapped}`;
          for (const path of takenPaths) {
            if (parentOfPath(path) === fullParentPath) {
              siblingNames.add(leafFromPath(path));
            }
          }
          const newLeaf = generateUniqueName(leaf, true, siblingNames);
          const newCandidate = `${parentPath}${parentMapped}${newLeaf}/`;
          takenPaths.add(newCandidate);
          folderOps.push({ path: newCandidate });
          folderRemap.set(folderRel, `${parentMapped}${newLeaf}/`);
          continue;
        }
      }

      // ── Phase 2: files ──────────────────────────────────────────
      for (const { file, relativePath } of tree.files) {
        if (isInsideSkippedPrefix(relativePath)) continue;

        const parentRel = parentRelOfFile(relativePath);
        // If the parent folder was supposed to be created but isn't in
        // the remap (e.g. tree.folders didn't list it — defensive),
        // fall back to identity.
        const parentMapped = folderRemap.get(parentRel) ?? parentRel;

        const planned = getPlannedUploadFile(file);
        const candidatePath = `${parentPath}${parentMapped}${planned.name}`;

        if (!takenPaths.has(candidatePath)) {
          takenPaths.add(candidatePath);
          fileOps.push({
            kind: "create",
            file,
            name: planned.name,
            contentType: planned.contentType,
            path: candidatePath,
          });
          continue;
        }

        let resolution: ConflictResolution;
        if (appliedFileAll) {
          resolution = appliedFileAll;
        } else {
          const result = await promptConflict(
            { sourceName: planned.name, isFolder: false },
            remainingFileConflicts,
          );
          if (result === "cancel") return null;
          resolution = result.resolution;
          if (result.applyToAll) appliedFileAll = resolution;
        }
        remainingFileConflicts = Math.max(0, remainingFileConflicts - 1);

        if (resolution === "merge") resolution = "keep_both";
        if (resolution === "skip") continue;

        if (resolution === "keep_both") {
          const siblingNames = new Set<string>();
          const fullParentPath = `${parentPath}${parentMapped}`;
          for (const path of takenPaths) {
            if (parentOfPath(path) === fullParentPath) {
              siblingNames.add(leafFromPath(path));
            }
          }
          const newLeaf = generateUniqueName(planned.name, false, siblingNames);
          const newPath = `${parentPath}${parentMapped}${newLeaf}`;
          takenPaths.add(newPath);
          fileOps.push({
            kind: "create",
            file,
            name: newLeaf,
            contentType: planned.contentType,
            path: newPath,
          });
          continue;
        }

        if (resolution === "replace_contents") {
          const existing = recordByPath.get(candidatePath);
          if (!existing) continue;
          fileOps.push({
            kind: "replace",
            file,
            name: planned.name,
            contentType: planned.contentType,
            path: candidatePath,
          });
          continue;
        }
      }

      return { folderOps, fileOps };
    },
    [queryClient, promptConflict],
  );

  const value = useMemo(
    () => ({
      resolveMoves,
      resolveUploads,
      resolveCopies,
      resolveFolderUpload,
    }),
    [resolveMoves, resolveUploads, resolveCopies, resolveFolderUpload],
  );

  return (
    <Context.Provider value={value}>
      {children}
      <FileConflictDialog
        conflict={active?.conflict ?? null}
        remainingCount={active?.remaining ?? 0}
        verb={active?.verb ?? "move"}
        onResolve={handleResolve}
        onCancelAll={handleCancel}
      />
    </Context.Provider>
  );
}

export function useFileConflictResolver() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error(
      "useFileConflictResolver must be used inside FileConflictResolverProvider",
    );
  }
  return ctx;
}

/**
 * Non-throwing variant: returns null when no provider is mounted (e.g. headless
 * canvas capture). Callers fall back to non-interactive behaviour.
 */
export function useFileConflictResolverSafe() {
  return useContext(Context);
}
